import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Database, Cloud, Mail, RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const ICONS = { mongodb: Database, cloudinary: Cloud, brevo: Mail };
const NAMES = { mongodb: "MongoDB", cloudinary: "Cloudinary", brevo: "Brevo (Email)" };

const STATUS_THEME = {
  ok:       { label: "Operational", className: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500", Icon: CheckCircle2 },
  degraded: { label: "Degraded",    className: "bg-amber-50 text-amber-900 border-amber-200",       dot: "bg-amber-500",   Icon: AlertTriangle },
  error:    { label: "Down",        className: "bg-red-50 text-red-900 border-red-200",             dot: "bg-red-500",     Icon: XCircle },
  disabled: { label: "Not configured", className: "bg-stone-50 text-stone-700 border-stone-200",    dot: "bg-stone-400",   Icon: MinusCircle },
};

export default function AdminSystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/system-health");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load system health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="system-health-loading">
        <h2 className="font-display text-xl font-medium">System health</h2>
        <div className="text-sm text-stone-500 mt-2">{loading ? "Probing services…" : "—"}</div>
      </div>
    );
  }

  const overall = STATUS_THEME[data.overall] || STATUS_THEME.error;

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="system-health-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-medium">System health</h2>
          <p className="text-xs text-stone-500 mt-1">
            Last checked {format(parseISO(data.checked_at), "MMM d, yyyy · h:mm:ss a")} ·
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ml-1.5 ${overall.className}`} data-testid="health-overall-pill">
              <span className={`w-1.5 h-1.5 rounded-full ${overall.dot} animate-pulse`} /> {overall.label}
            </span>
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full border-stone-300 h-8"
          onClick={load}
          disabled={loading}
          data-testid="health-refresh-button"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-5">
        {data.probes.map((p) => (
          <ProbeCard key={p.name} probe={p} />
        ))}
      </div>
    </div>
  );
}

function ProbeCard({ probe }) {
  const Icon = ICONS[probe.name] || Database;
  const theme = STATUS_THEME[probe.status] || STATUS_THEME.error;
  const StatusIcon = theme.Icon;
  return (
    <div className="border border-stone-200 rounded-xl p-4 bg-stone-50/40" data-testid={`probe-${probe.name}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-stone-500" />
          <h3 className="font-medium text-stone-900">{NAMES[probe.name] || probe.name}</h3>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full border ${theme.className}`} data-testid={`probe-${probe.name}-status`}>
          <StatusIcon className="w-3 h-3" /> {theme.label}
        </span>
      </div>
      <div className="text-sm text-stone-700">{probe.summary || "—"}</div>
      <div className="text-[11px] text-stone-500 mt-1">{probe.latency_ms || 0} ms response</div>
      <DetailGrid name={probe.name} details={probe.details || {}} />
    </div>
  );
}

function DetailGrid({ name, details }) {
  const rows = formatDetails(name, details);
  if (rows.length === 0) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] mt-3 pt-3 border-t border-stone-200/70">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-stone-500">{k}</dt>
          <dd className="text-stone-800 tabular-nums text-right truncate" title={String(v)}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatDetails(name, d) {
  if (name === "mongodb") {
    return [
      ["Database", d.db],
      ["Collections", d.collections?.toLocaleString()],
      ["Documents", d.objects?.toLocaleString()],
      ["Data size", `${d.data_size_mb} MB`],
      ["Storage", `${d.storage_size_mb} MB`],
      ["Indexes", d.indexes?.toLocaleString()],
    ].filter(([, v]) => v !== undefined && v !== null);
  }
  if (name === "cloudinary") {
    const credits = d.credits_limit ? `${d.credits_used} / ${d.credits_limit} (${d.credits_used_percent.toFixed(1)}%)` : `${d.credits_used}`;
    return [
      ["Plan", d.plan],
      ["Resources", d.resources?.toLocaleString()],
      ["Transformations", d.transformations_used?.toLocaleString()],
      ["Credits", credits],
      ["Storage", `${d.storage_used_gb} GB`],
      ["Bandwidth", `${d.bandwidth_used_gb} GB`],
    ].filter(([, v]) => v !== undefined && v !== null);
  }
  if (name === "brevo") {
    return [
      ["Plan", d.plan],
      ["Credits left", d.credits_remaining?.toLocaleString()],
      ["Account", d.account_email],
      ["Company", d.company],
    ].filter(([, v]) => v !== undefined && v !== null && v !== "");
  }
  return [];
}
